import type { Basket } from '~/types/basket'
import type { Order } from '~/types/order'
import type { Product } from '~/types/product'

export function useDataLayers() {
  const {
    public: { appEnvironment },
  } = useRuntimeConfig()
  const auth = useAuth()
  const route = useRoute()
  const {
    $i18n: { locale },
  } = useNuxtApp()
  const { currency } = useCurrency()
  const { basket } = useBasketService()

  /**
   * Returns an object with all product categories, formatted for dataLayer.
   * @param {Product} product - The product to extract categories from.
   * @returns {Record<string, string>} Object with category keys and titles.
   */
  function getAllCategories(product: Product): Record<string, string> {
    const categories: string[] = []

    for (const category in product?.categories) {
      const titles = product.categories[category]?.map((item) => item.title) ?? []
      categories.push(...titles)
    }

    const categoryObject: { [key: string]: string } = {}
    categories.forEach((title, index) => {
      if (index > 0) {
        categoryObject[`item_category${index + 1}`] = title
      } else {
        categoryObject['item_category'] = title
      }
    })

    return categoryObject
  }

  /**
   * Returns a formatted item object for the dataLayer.
   * @param {Product} product - The product to format.
   * @param {number} [index] - The index of the item in the list.
   * @param {number} [variantIndex] - The index of the variant, if applicable.
   * @returns {object} The formatted item object.
   */
  const getDataLayerItem = (product: Product, index?: number, variantIndex?: number): object => {
    const isVariant = variantIndex !== undefined
    const fullTitle = `${product?.shortTitle ? `${product.shortTitle} - ` : ''}${product.title}`
    // TODO discount, coupon?
    return {
      index,
      item_id: product.id,
      item_name: fullTitle,
      price: isVariant
        ? (product.variants[variantIndex]?.prices?.price?.amount ?? null)
        : (product.prices?.price?.amount ?? null),
      currency: currency.value,
      item_variant: isVariant ? product.variants[variantIndex]?.title : undefined,
      // Kategorie
      ...getAllCategories(product),
    }
  }

  /**
   * Pushes a page_init event to the dataLayer.
   * No parameters.
   * @returns {void}
   */
  const pageInit = (): void => {
    onNuxtReady(() => {
      window.dataLayer.push({
        event: 'page_init',
        language: locale.value,
        currency: currency.value,
        page_type: route.meta.pageType ?? null,
        environment: appEnvironment,
        user: auth.value.user
          ? {
              user_name: auth.value.user.profile.name,
              user_email: auth.value.user.profile.email,
              user_phone: auth.value.user.profile.phone,
              user_country: auth.value.user.profile.country,
              user_id: auth.value.user.profile.id,
              user_cart_value: basket.value.price,
              user_cart_items: basket.value.count,
            }
          : null,
      })
    })
  }

  /**
   * Pushes a view_item_list event to the dataLayer.
   * @param {string} id - The list ID.
   * @param {string} name - The list name.
   * @param {Product[]} products - The products in the list.
   * @returns {void}
   */
  const viewList = (id: string, name: string, products: Product[]): void => {
    onNuxtReady(() => {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          item_list_id: id,
          item_list_name: name,
          currency: currency.value,
          items: products.map((product, i) => getDataLayerItem(product, i)),
        },
      })
    })
  }

  /**
   * Pushes a select_item event to the dataLayer.
   * @param {string} id - The list ID.
   * @param {string} name - The list name.
   * @param {Product} product - The selected product.
   * @param {number} index - The index of the selected item.
   * @returns {void}
   */
  const selectItem = (id: string, name: string, product: Product, index: number): void => {
    onNuxtReady(() => {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_id: id,
          item_list_name: name,
          items: [getDataLayerItem(product, index)],
        },
      })
    })
  }

  /**
   * Pushes a view_item event to the dataLayer.
   * @param {Product} product - The product to view.
   * @returns {void}
   */
  const viewItem = (product: Product): void => {
    onNuxtReady(() => {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'view_item',
        ecommerce: {
          currency: currency.value,
          value: product.variants[0]?.prices?.price?.amount,
          value_vat: product.variants[0]?.prices?.priceVat?.amount,
          items: [getDataLayerItem(product, undefined, 0)],
        },
      })
    })
  }

  /**
   * Pushes a view_cart event to the dataLayer.
   * @param {Basket} basket - The basket to view.
   * @returns {void}
   */
  const viewCart = (basket: Basket): void => {
    onNuxtReady(() => {
      const basketItems = basket.basketItems
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'view_cart',
        ecommerce: {
          currency: basket?.currency,
          value: basket?.totalPrice?.amount,
          value_vat: basket?.totalPriceVat.amount,
          items: basketItems
            ?.filter((item) => item?.product?.id)
            ?.map((item, index) => ({
              ...getDataLayerItem(item.product, index),
              price: item.price?.amount,
              currency: item.priceVat.currency,
              discount: item?.discount?.value,
              quantity: item.quantity,
            })),
        },
      })
    })
  }

  /**
   * Pushes an add_to_cart event to the dataLayer.
   * @param {Product} product - The product to add.
   * @param {number} variantIndex - The index of the variant.
   * @param {number} quantity - The quantity to add.
   * @returns {void}
   */
  const addToCart = (product: Product, variantIndex: number, quantity: number): void => {
    onNuxtReady(() => {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: product.variants[variantIndex]?.prices?.price?.currency,
          value: product.variants[variantIndex]?.prices?.price?.amount,
          value_vat: product.variants[variantIndex]?.prices?.priceVat?.amount,
          items: [
            {
              ...getDataLayerItem(product, undefined, variantIndex),
              quantity,
            },
          ],
        },
      })
    })
  }

  /**
   * Pushes a remove_from_cart event to the dataLayer.
   * @param {Product} product - The product to remove.
   * @param {number} variantIndex - The index of the variant.
   * @returns {void}
   */
  const removeFromCart = (product: Product, variantIndex: number): void => {
    onNuxtReady(() => {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          currency: product.variants[variantIndex]?.prices?.price?.currency,
          value: product.variants[variantIndex]?.prices?.price?.amount,
          value_vat: product.variants[variantIndex]?.prices?.priceVat?.amount,
          items: [getDataLayerItem(product, undefined, variantIndex)],
        },
      })
    })
  }

  /**
   * Pushes a begin_checkout event to the dataLayer.
   * @param {Basket} basket - The basket to checkout.
   * @returns {void}
   */
  const beginCheckout = (basket: Basket): void => {
    onNuxtReady(() => {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          coupon: basket.discounts?.coupons?.length ? basket.discounts.coupons[0].code : null,
          currency: basket?.currency,
          value: basket?.totalPrice?.amount,
          value_vat: basket?.totalPriceVat.amount,
          items: basket.basketItems
            ?.filter((item) => item?.product?.id)
            ?.map((item, index) => ({
              ...getDataLayerItem(item.product, index),
              price: item.price?.amount,
              currency: item.priceVat.currency,
              discount: item?.discount?.value,
              quantity: item.quantity,
            })),
        },
      })
    })
  }

  /**
   * Pushes shipping information to the global `dataLayer` for ecommerce tracking.
   * @param {Basket} basket - The current basket containing delivery, pricing, and item information.
   * @param {any} selectedDelivery - The selected delivery method.
   * @returns {void}
   */
  const addShippingInfo = (basket: Basket, selectedDelivery: any): void => {
    onNuxtReady(() => {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'add_shipping_info',
        ecommerce: {
          shipping_tier: basket.deliveries?.tariff?.name,
          coupon: basket.discounts?.coupons?.length ? basket.discounts.coupons[0].code : null,
          currency: basket?.currency,
          value: selectedDelivery.price?.amount,
          value_vat: selectedDelivery.priceVat?.amount,
          items: basket.basketItems
            ?.filter((item) => item?.product?.id)
            ?.map((item, index) => ({
              ...getDataLayerItem(item.product, index),
              price: item.price?.amount,
              currency: item.priceVat.currency,
              discount: item?.discount?.value,
              quantity: item.quantity,
            })),
        },
      })
    })
  }

  /**
   * Pushes payment information to the dataLayer for analytics tracking.
   * @param {Basket} basket - The current basket containing payment and item details.
   * @param {any} payment - The selected payment method.
   * @returns {void}
   */
  const addPaymentInfo = (basket: Basket, payment: any): void => {
    onNuxtReady(() => {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'add_payment_info',
        ecommerce: {
          payment_type: basket?.payment?.isOnline ? 'Credit Card' : 'Cash',
          coupon: basket.discounts?.coupons?.length ? basket.discounts.coupons[0].code : null,
          currency: basket?.currency,
          value: payment?.prices?.price?.amount,
          value_vat: payment?.prices?.priceVat?.amount,
          items: basket.basketItems
            ?.filter((item) => item?.product?.id)
            ?.map((item, index) => ({
              ...getDataLayerItem(item.product, index),
              price: item.price?.amount,
              currency: item.priceVat.currency,
              discount: item?.discount?.value,
              quantity: item.quantity,
            })),
        },
      })
    })
  }

  /**
   * Pushes a purchase event to the dataLayer for ecommerce tracking.
   * @param {Order} order - The order object containing purchase details.
   * @returns {void}
   */
  const purchase = (order: Order): void => {
    onNuxtReady(() => {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: order.id,
          affiliation: 'Belles Fleurs',
          currency: order.currency,
          value: order.prices?.total?.amount,
          value_vat: order.prices?.totalPriceVat?.amount,
          tax: order.prices?.vat?.amount,
          shipping: order?.shipping?.price?.amount,
          items: order.items.children
            ?.filter((item) => item.data?.product?.id)
            ?.map((item, index) => ({
              ...getDataLayerItem(item.data.product, index),
              price: item.data.price?.amount,
              currency: item.data.priceVat.currency,
              discount: item.data?.discount?.value,
              quantity: item.data.quantity,
            })),
        },
        user: {},
      })
    })
  }

  /**
   * Pushes a user_login event to the dataLayer.
   * @param {User} user - The user who logged in.
   * @returns {void}
   */
  const userLogin = (user: User): void => {
    onNuxtReady(() => {
      window.dataLayer.push({
        event: 'user_login',
        user: {
          user_id: user?.id,
          user_email: user?.email,
          user_name: user?.profile.name,
          user_phone: user?.profile.phone,
          user_country: user?.profile.country,
        },
      })
    })
  }

  return {
    pageInit,
    viewList,
    selectItem,
    viewItem,
    viewCart,
    addToCart,
    removeFromCart,
    beginCheckout,
    addShippingInfo,
    addPaymentInfo,
    purchase,
    userLogin,
  }
}
